import { Orientation, directionOrientation, } from "@buildwithflux/models";
import { keySort } from "@buildwithflux/shared";
import { box2 } from "../common";
const { HORIZONTAL } = Orientation;
export class CustomSymbolLayout {
    static estimateSymbolBounds(terminalPositions) {
        // Include (0,0) in the bounds below, so elements with terminals on only 1 side still get a meaningful box.
        let outerBounds = box2.fromPoints([{ x: 0, y: 0 }, ...terminalPositions]);
        const size = box2.size(outerBounds);
        if (size.y === 0 && size.x > 0) {
            const center = box2.center(outerBounds);
            outerBounds = {
                min: { x: outerBounds.min.x, y: center.y - size.x / 2 },
                max: { x: outerBounds.max.x, y: center.y + size.x / 2 },
            };
        }
        else if (size.x === 0 && size.y > 0) {
            const center = box2.center(outerBounds);
            outerBounds = {
                min: { x: center.x - size.y / 2, y: outerBounds.min.y },
                max: { x: center.x + size.y / 2, y: outerBounds.max.y },
            };
        }
        else if (terminalPositions.length >= 6) {
            // magic number of `terminalPositions.length >= 6` above was chosen by matthias and
            // devin in https://github.com/buildwithflux/flux-app/pull/19699 as a quick way to
            // detect IC type symbols. The reason we didn't use "part type" for this is that
            // sometimes things like relays and diodes use an IC style symbol, so could be
            // miscategorised.
            const verticalMargin = this.calculateVerticalTerminalMargin(terminalPositions);
            if (verticalMargin > 0) {
                outerBounds = {
                    min: { x: outerBounds.min.x, y: outerBounds.min.y - verticalMargin },
                    max: { x: outerBounds.max.x, y: outerBounds.max.y + verticalMargin },
                };
            }
        }
        return outerBounds;
    }
    static calculateSingleSymbolLayout(symbolName, pins, asset, accurateBounds) {
        const terminalPositions = pins.map((t) => t.position ?? { x: 0, y: 0 });
        let outerBounds;
        if (accurateBounds) {
            // Use the accurate bounds from rendered SVG
            outerBounds = accurateBounds;
        }
        else {
            // Estimate bounds from terminal positions, then add padding for the symbol body.
            // Symbol SVGs extend beyond the terminal connection points, so we need to add
            // padding to account for the actual symbol size when accurate bounds aren't available.
            outerBounds = this.estimateSymbolBounds(terminalPositions);
            const symbolPadding = 24;
            outerBounds = {
                min: { x: outerBounds.min.x - symbolPadding, y: outerBounds.min.y - symbolPadding },
                max: { x: outerBounds.max.x + symbolPadding, y: outerBounds.max.y + symbolPadding },
            };
        }
        return {
            name: symbolName,
            outlineSize: box2.size(outerBounds),
            outerBounds,
            pinLocations: CustomSymbolLayout.getTerminalPlacements(pins, outerBounds),
            pins: pins,
            sections: [],
            // Custom symbols include their pins as part of the symbol:
            pinLength: 0,
            symbolStyle: "custom",
            assetFilename: asset.storageName,
        };
    }
    static calculateVerticalTerminalMargin(terminalPositions) {
        if (terminalPositions.length < 2) {
            return 0;
        }
        const sortedYPositions = terminalPositions.map((p) => p.y).sort((a, b) => a - b);
        let minGap = Infinity;
        for (let i = 1; i < sortedYPositions.length; i++) {
            const gap = sortedYPositions[i] - sortedYPositions[i - 1];
            if (gap > 0 && gap < minGap) {
                minGap = gap;
            }
        }
        return minGap === Infinity ? 0 : minGap;
    }
    static getTerminalPlacements(terminals, bounds) {
        const terminalPositions = Object.fromEntries(terminals.map((t) => [t.uid, (t.position ?? { x: 0, y: 0 })]));
        const placements = {};
        for (const [terminalUid, terminalPosition] of Object.entries(terminalPositions)) {
            // `directionMagnitudes` is not quite the same as "distance from given edge" --
            // imagine a tall-thin component, with a top-oriented terminal. The terminal
            // might be closer to the left edge, but it's actually above the top edge,
            // which makes it top-oriented.
            const directionMagnitudes = {
                left: bounds.min.x - terminalPosition.x,
                right: terminalPosition.x - bounds.max.x,
                up: terminalPosition.y - bounds.max.y,
                down: bounds.min.y - terminalPosition.y,
            };
            if (bounds.min.x === bounds.max.x) {
                // If the bounds are a vertical line, only up/down terminals are valid
                directionMagnitudes.left = directionMagnitudes.right = -Infinity;
            }
            if (bounds.min.y === bounds.max.y) {
                // Equivalient for horizontal line
                directionMagnitudes.up = directionMagnitudes.down = -Infinity;
            }
            // Sort by left/right orientation, then decreasing magnitude, so if two are at equal
            // distance (i.e. they're on a bounds corner -- common occurence when guessing bounds
            // from terminals) they're assumed to be horizontal.
            const sortedMagnitudes = Object.entries(directionMagnitudes)
                .sort(keySort(([d, _]) => (directionOrientation(d) === HORIZONTAL ? 0 : 1)))
                .sort(keySort(([_, v]) => -v));
            const direction = sortedMagnitudes[0]?.[0] ?? "left";
            placements[terminalUid] = { position: terminalPosition, direction };
        }
        return placements;
    }
}
//# sourceMappingURL=CustomSymbolLayout.js.map